import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react'
import type { ReactNode } from 'react'
import type { GenerateProgressEvent, FeedbackProgressEvent, OutsideInJobState, InsideOutJobState, GccJobState, GccProgressEvent } from '@shared/types'
import { useApi } from './ApiContext'
import type { IApiClient } from '../api/IApiClient'

const STORAGE_KEY = 'tn_active_generations'
const COMPLETED_KEY = 'tn_generation_completed_at'
const POLL_INTERVAL = 3000

export interface TrackedJob {
  jobId: string
  projectId: string
  type: 'outside-in' | 'inside-out' | 'gcc' | 'feedback'
  clientName: string
  color: string
  status: 'pending' | 'running' | 'succeeded' | 'failed'
  /** Used by outside-in and inside-out jobs. Empty for gcc and feedback jobs. */
  progress: GenerateProgressEvent[]
  /** Used by gcc jobs only. */
  gccProgress?: GccProgressEvent[]
  /** Used by feedback jobs only. */
  feedbackProgress?: FeedbackProgressEvent[]
  error?: string
  startedAt: string
}

interface PersistedEntry {
  jobId: string
  projectId: string
  type: 'outside-in' | 'inside-out' | 'gcc' | 'feedback'
  clientName: string
  color: string
  startedAt: string
}

interface GenerationContextValue {
  activeJobs: TrackedJob[]
  synced: boolean
  trackJob: (jobId: string, projectId: string, type: 'outside-in' | 'inside-out' | 'gcc' | 'feedback', clientName: string, color: string) => void
  removeJob: (jobId: string) => void
  cancelJob: (jobId: string) => Promise<void>
  generationCompletedAt: number | null
  /** Project IDs whose most-recent feedback job succeeded. Cleared by clearFeedbackCompleted. */
  feedbackCompletedProjects: Set<string>
  clearFeedbackCompleted: (projectId: string) => void
}

const GenerationContext = createContext<GenerationContextValue>({
  activeJobs: [],
  synced: false,
  trackJob: () => {},
  removeJob: () => {},
  cancelJob: async () => {},
  generationCompletedAt: null,
  feedbackCompletedProjects: new Set(),
  clearFeedbackCompleted: () => {},
})

/**
 * Remove the skeleton project a failed Outside-In / Inside-Out run left behind.
 * A TN analysis started from a GCC design is kept, so its card can show Failed
 * and the user can delete it themselves (WI 2399).
 */
export async function deleteFailedSkeleton(
  api: Pick<IApiClient, 'getProject' | 'deleteProject'>,
  projectId: string
): Promise<void> {
  const project = await api.getProject(projectId).catch(() => null)
  if (project?.sourceGccProjectId) return
  await api.deleteProject(projectId)
}

export function GenerationProvider({ children }: { children: ReactNode }) {
  const api = useApi()
  const [jobs, setJobs] = useState<TrackedJob[]>([])
  const [synced, setSynced] = useState(false)
  const [generationCompletedAt, setGenerationCompletedAt] = useState<number | null>(null)
  const [feedbackCompletedProjects, setFeedbackCompletedProjects] = useState<Set<string>>(new Set())
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const jobsRef = useRef<TrackedJob[]>([])
  jobsRef.current = jobs
  // localStorage jobs held here until syncWithServer validates they belong to the
  // current user — never committed to state directly to avoid other users' jobs flashing.
  const pendingRestoredRef = useRef<TrackedJob[]>([])
  const restoredFromStorageRef = useRef<Set<string>>(new Set())

  // Load persisted job IDs on mount into a ref — NOT into state yet.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (raw) {
        const persisted = JSON.parse(raw) as PersistedEntry[]
        const restored: TrackedJob[] = persisted.map((e) => ({
          ...e,
          status: 'pending' as const,
          progress: [],
        }))
        restoredFromStorageRef.current = new Set(restored.map((j) => j.jobId))
        pendingRestoredRef.current = restored
      }
    } catch { /* ignore */ }
  }, [])

  // Persist active job IDs whenever jobs change
  useEffect(() => {
    const toStore: PersistedEntry[] = jobs
      .filter((j) => j.status === 'pending' || j.status === 'running')
      .map(({ jobId, projectId, type, clientName, color, startedAt }) => ({
        jobId, projectId, type, clientName, color, startedAt,
      }))
    try {
      if (toStore.length > 0) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore))
      } else {
        localStorage.removeItem(STORAGE_KEY)
      }
    } catch { /* ignore */ }
  }, [jobs])

  // Notify other browser tabs when this tab detects a generation completed
  useEffect(() => {
    if (!generationCompletedAt) return
    try {
      if (localStorage.getItem(COMPLETED_KEY) !== String(generationCompletedAt)) {
        localStorage.setItem(COMPLETED_KEY, String(generationCompletedAt))
      }
    } catch { /* ignore */ }
  }, [generationCompletedAt])

  // Pick up generation-completed signals from other browser tabs
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key !== COMPLETED_KEY || !e.newValue) return
      const ts = Number(e.newValue)
      if (!isNaN(ts)) setGenerationCompletedAt(ts)
    }
    window.addEventListener('storage', handler)
    return () => window.removeEventListener('storage', handler)
  }, [])

  // Polling loop — uses jobsRef to avoid stale closure
  useEffect(() => {
    const pollJobs = async () => {
      const active = jobsRef.current.filter((j) => j.status === 'pending' || j.status === 'running')
      if (active.length === 0) return

      window.dispatchEvent(new CustomEvent('tn:generation-keepalive'))

      await Promise.all(
        active.map(async (job) => {
          try {
            if (job.type === 'feedback') {
              const state = await (api.getFeedbackJob?.(job.jobId) ?? Promise.resolve(null))

              if (!state) {
                setJobs((prev) => prev.filter((j) => j.jobId !== job.jobId))
                setGenerationCompletedAt(Date.now())
                return
              }

              setJobs((prev) =>
                prev.map((j) => {
                  if (j.jobId !== job.jobId) return j
                  return { ...j, status: state.status, feedbackProgress: state.progress, error: state.error }
                })
              )

              if (state.status === 'succeeded' || state.status === 'failed') {
                setGenerationCompletedAt(Date.now())
                // Don't delete the project on feedback failure — existing data remains intact.
                // Record successful completions so the Dashboard can show a notification even
                // after the user navigates away and returns.
                if (state.status === 'succeeded') {
                  setFeedbackCompletedProjects((prev) => new Set([...prev, job.projectId]))
                }
              }
            } else if (job.type === 'gcc') {
              const state: GccJobState | null = await (api.getGccJob?.(job.jobId) ?? Promise.resolve(null))

              if (!state) {
                setJobs((prev) => prev.filter((j) => j.jobId !== job.jobId))
                setGenerationCompletedAt(Date.now())
                return
              }

              setJobs((prev) =>
                prev.map((j) => {
                  if (j.jobId !== job.jobId) return j
                  return { ...j, status: state.status, gccProgress: state.progress, error: state.error }
                })
              )

              if (state.status === 'succeeded' || state.status === 'failed') {
                setGenerationCompletedAt(Date.now())
                if (state.status === 'failed') {
                  void api.deleteProject(job.projectId).catch(() => {})
                }
              }
            } else {
              const state: OutsideInJobState | InsideOutJobState | null = job.type === 'outside-in'
                ? await api.getOutsideInJob(job.jobId)
                : await api.getInsideOutJob(job.jobId)

              if (!state) {
                // Job not found — server restarted or expired; drop from tracking.
                // Also trigger a refresh in case the job completed before cleanup.
                setJobs((prev) => prev.filter((j) => j.jobId !== job.jobId))
                setGenerationCompletedAt(Date.now())
                return
              }

              const { status, progress, error } = state
              setJobs((prev) =>
                prev.map((j) => {
                  if (j.jobId !== job.jobId) return j
                  return { ...j, status, progress, error }
                })
              )

              if (state.status === 'succeeded' || state.status === 'failed') {
                setGenerationCompletedAt(Date.now())
                if (state.status === 'failed') {
                  void deleteFailedSkeleton(api, job.projectId).catch(() => {})
                }
              }
            }
          } catch { /* ignore transient poll errors */ }
        })
      )
    }

    // On mount, ask the server for any active jobs this browser lost track of
    // (e.g. after a tab close, page refresh in a new session, or server restart).
    const syncWithServer = async () => {
      try {
        const serverJobs = await api.getMyActiveJobs()
        const serverIds = new Set(serverJobs.map((sj) => sj.jobId))

        // Commit only the localStorage-restored jobs the server recognises for
        // this user. localStorage is shared per-browser; jobs from another user
        // were never added to state, so no prune step is needed — just add the valid ones.
        const pendingRestored = pendingRestoredRef.current
        if (pendingRestored.length > 0) {
          pendingRestoredRef.current = []
          restoredFromStorageRef.current = new Set()
          const validRestored = pendingRestored.filter((j) => serverIds.has(j.jobId))
          if (validRestored.length > 0) {
            setJobs((prev) => {
              const ids = new Set(prev.map((j) => j.jobId))
              return [...prev, ...validRestored.filter((j) => !ids.has(j.jobId))]
            })
          }
        }

        if (serverJobs.length === 0) return
        // Track IDs already in state plus those we just queued via validRestored.
        const existingIds = new Set([
          ...jobsRef.current.map((j) => j.jobId),
          ...pendingRestored.map((j) => j.jobId),
        ])
        const newJobs = serverJobs.filter((sj) => !existingIds.has(sj.jobId))
        if (newJobs.length === 0) return
        // Fetch actual job state immediately so progress doesn't reset to 0% after a page refresh.
        const resolved = await Promise.all(
          newJobs.map(async (sj): Promise<TrackedJob> => {
            try {
              if (sj.type === 'feedback') {
                const state = await (api.getFeedbackJob?.(sj.jobId) ?? Promise.resolve(null))
                return {
                  jobId: sj.jobId,
                  projectId: sj.projectId,
                  type: 'feedback',
                  clientName: sj.clientName,
                  color: sj.color,
                  status: state?.status ?? 'running',
                  progress: [],
                  feedbackProgress: state?.progress ?? [],
                  error: state?.error,
                  startedAt: state?.createdAt ?? new Date().toISOString(),
                }
              }
              if (sj.type === 'gcc') {
                const state = await (api.getGccJob?.(sj.jobId) ?? Promise.resolve(null))
                return {
                  jobId: sj.jobId,
                  projectId: sj.projectId,
                  type: 'gcc',
                  clientName: sj.clientName,
                  color: sj.color,
                  status: state?.status ?? 'running',
                  progress: [],
                  gccProgress: state?.progress ?? [],
                  error: state?.error,
                  startedAt: new Date().toISOString(),
                }
              }
              const state = sj.type === 'outside-in'
                ? await api.getOutsideInJob(sj.jobId)
                : await api.getInsideOutJob(sj.jobId)
              return {
                jobId: sj.jobId,
                projectId: sj.projectId,
                type: sj.type,
                clientName: sj.clientName,
                color: sj.color,
                status: state?.status ?? 'running',
                progress: state?.progress ?? [],
                error: state?.error,
                startedAt: new Date().toISOString(),
              }
            } catch {
              return {
                jobId: sj.jobId,
                projectId: sj.projectId,
                type: sj.type,
                clientName: sj.clientName,
                color: sj.color,
                status: 'running',
                progress: [],
                startedAt: new Date().toISOString(),
              }
            }
          })
        )
        setJobs((prev) => {
          const ids = new Set(prev.map((j) => j.jobId))
          const toAdd = resolved.filter((j) => !ids.has(j.jobId))
          return toAdd.length > 0 ? [...prev, ...toAdd] : prev
        })
      } catch { /* ignore */ } finally {
        setSynced(true)
      }
    }

    if (pollRef.current) clearInterval(pollRef.current)
    pollRef.current = setInterval(() => void pollJobs(), POLL_INTERVAL)
    void syncWithServer()
    void pollJobs()

    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [api])

  const trackJob = useCallback(
    (jobId: string, projectId: string, type: 'outside-in' | 'inside-out' | 'gcc' | 'feedback', clientName: string, color: string) => {
      const entry: TrackedJob = {
        jobId, projectId, type, clientName, color,
        status: 'running',
        progress: [],
        startedAt: new Date().toISOString(),
      }
      setJobs((prev) => {
        const filtered = prev.filter((j) => j.jobId !== jobId)
        return [...filtered, entry]
      })
    },
    []
  )

  const removeJob = useCallback((jobId: string) => {
    setJobs((prev) => prev.filter((j) => j.jobId !== jobId))
  }, [])

  const clearFeedbackCompleted = useCallback((projectId: string) => {
    setFeedbackCompletedProjects((prev) => {
      const next = new Set(prev)
      next.delete(projectId)
      return next
    })
  }, [])

  const cancelJob = useCallback(async (jobId: string) => {
    const job = jobsRef.current.find((j) => j.jobId === jobId)
    if (!job) return
    // Feedback cancel button is hidden in the UI. If called, remove from tracking silently —
    // the server job cannot be stopped and changes will still apply.
    if (job.type === 'feedback') {
      setJobs((prev) => prev.filter((j) => j.jobId !== jobId))
      return
    }
    // Optimistically mark failed so the UI responds immediately without waiting for the next poll
    setJobs((prev) =>
      prev.map((j) =>
        j.jobId === jobId ? { ...j, status: 'failed' as const, error: 'Cancelled by user.' } : j
      )
    )
    setGenerationCompletedAt(Date.now())

    // Delete the skeleton project and cancel the server job in parallel.
    // Both are awaited so callers can rely on completion before refreshing the project list.
    let cancelCall: Promise<void>
    if (job.type === 'gcc') {
      cancelCall = api.cancelGccJob?.(jobId) ?? Promise.resolve()
    } else if (job.type === 'outside-in') {
      cancelCall = api.cancelOutsideInJob(jobId)
    } else {
      cancelCall = api.cancelInsideOutJob(jobId)
    }
    await Promise.all([
      api.deleteProject(job.projectId).catch(() => {}),
      cancelCall.catch(() => {}),
    ])
  }, [api])

  return (
    <GenerationContext.Provider value={{ activeJobs: jobs, synced, trackJob, removeJob, cancelJob, generationCompletedAt, feedbackCompletedProjects, clearFeedbackCompleted }}>
      {children}
    </GenerationContext.Provider>
  )
}

export function useGenerationContext() {
  return useContext(GenerationContext)
}

const NON_SUBFUNCTION_LABELS = new Set([
  'AI & Automation Portfolio',
  'Process Redesign Roadmap',
  'Generating automation builds…',
  'Generating process redesign roadmap…',
])

function isSubfunctionEvent(e: GenerateProgressEvent): boolean {
  return (
    !NON_SUBFUNCTION_LABELS.has(e.subfunctionName) &&
    !e.subfunctionName.startsWith('Researching ') &&
    e.subfunctionName !== 'Analyzing document structure…'
  )
}

const INSIDE_OUT_STAGE_LABELS: Record<number, string> = {
  1: 'Generating Org Structure & Data Gaps',
  2: 'Generating Role Detail & Capacity',
  3: 'Generating Future Workforce Archetypes',
  4: 'Building Process Redesign Roadmap',
  5: 'Generating Automation Build Portfolio',
  6: 'Synthesising Narratives',
}

/**
 * Compute display progress for a tracked job.
 *
 * For gcc jobs, pass gccProgress (from TrackedJob.gccProgress) instead of progress.
 * For feedback jobs, pass feedbackProgress (from TrackedJob.feedbackProgress).
 * For outside-in and inside-out, pass progress and the type discriminant.
 */
export function computeProgress(
  progress: GenerateProgressEvent[],
  type: 'outside-in' | 'inside-out' | 'gcc' | 'feedback' = 'outside-in',
  gccProgress?: GccProgressEvent[],
  feedbackProgress?: FeedbackProgressEvent[]
): {
  pct: number
  done: number
  total: number
  label: string
} {
  if (type === 'feedback') {
    const events = feedbackProgress ?? []
    if (events.length === 0) return { pct: 0, done: 0, total: 0, label: 'Applying changes…' }
    const total = events.length
    const done = events.filter((e) => e.status === 'done').length
    const running = events.find((e) => e.status === 'generating')
    const pct = Math.min(95, Math.round(((done + (running ? 0.5 : 0)) / total) * 100))
    const label = running ? running.label : done > 0 ? 'Finalizing…' : 'Applying changes…'
    return { pct, done, total, label }
  }

  if (type === 'gcc') {
    const events = gccProgress ?? []
    if (events.length === 0) return { pct: 0, done: 0, total: 4, label: 'Starting…' }
    // Steps 1–4 count toward the progress bar; step 0 (task register) is initialisation.
    const TOTAL = 4
    const done = events.filter((p) => p.status === 'done' && p.step > 0).length
    const running = events.find((p) => p.status === 'running')
    const pct = Math.min(95, Math.round(((done + (running ? 0.5 : 0)) / TOTAL) * 100))
    const label = running ? running.label : done > 0 ? 'Finalizing…' : 'Starting…'
    return { pct, done, total: TOTAL, label }
  }

  if (progress.length === 0) return { pct: 0, done: 0, total: 0, label: 'Starting…' }

  if (type === 'inside-out') {
    const TOTAL = 6
    const done = progress.filter((p) => p.status === 'done').length
    const generating = progress.find((p) => p.status === 'generating')
    const pct = Math.min(95, Math.round(((done + (generating ? 0.5 : 0)) / TOTAL) * 100))
    const label = generating
      ? (INSIDE_OUT_STAGE_LABELS[generating.index] ?? `Stage ${generating.index}`)
      : done > 0 ? 'Finalizing…' : 'Starting…'
    return { pct, done, total: TOTAL, label }
  }

  const sfEvents = progress.filter(isSubfunctionEvent)
  const total = sfEvents.length > 0 ? Math.max(...sfEvents.map((p) => p.total)) : 0
  const done = sfEvents.filter((p) => p.status === 'done').length

  const allTotal = Math.max(...progress.map((p) => p.total))
  const allDone = progress.filter((p) => p.status === 'done').length
  const allGenerating = progress.filter((p) => p.status === 'generating').length
  const pct = Math.min(95, Math.round(((allDone + allGenerating * 0.5) / allTotal) * 100))

  const current = progress.find((p) => p.status === 'generating')
  let label: string
  if (!current) {
    label = allDone > 0 ? 'Finalizing…' : 'Queued…'
  } else if (!isSubfunctionEvent(current)) {
    label = current.subfunctionName
  } else {
    label = `${done} of ${total} subfunctions`
  }

  return { pct, done, total, label }
}
